/* Build in libraries */
import { Link, useLocation } from 'react-router-dom';

/* Third party libraries */
import { XIcon } from '@heroicons/react/outline';
import { LocationMarkerIcon } from '@heroicons/react/solid';

/* Components */
import TotalApplicationsWidget from 'components/Rover/Widgets/TotalApplicationsWidget';
import Card from 'components/UI/Card';
import PieChart from 'components/UI/Charts/PieChart';
import ToBeInspectedWidget from 'components/Rover/Widgets/ToBeInspectedWidget';
import TotalDeniedRulesWidget from 'components/Rover/Widgets/TotalDeniedRulesWidget';
import { PhoneNumberFormatDisplay } from 'components/UI/NumberFormatDisplay/PhoneNumberFormatDisplay';
import LocationMarker from 'components/UI/GoogleMap/LocationMarker';
import GoogleMap from 'components/UI/GoogleMap';
import DistrictBreakdownDeniedWidget from 'components/Rover/Widgets/DistrictBreakdownDeniedWidget';

/* Services */
import { EApplicationStatus } from 'features/ApplicationForm/constants';
import { convertEnumToText, isObjectEmpty } from 'utils';
import { convertIntoPercentage } from './helpers';

export function CRAllApplications({
  data: widgetsData,
  applicationsGeoLocated,
  selectedProject,
  setSelectedProject,
}: {
  applicationsGeoLocated: any[];
  data?: Record<string, any>;
  selectedProject: any;
  setSelectedProject: (value: any) => void;
}) {
  const location = useLocation();

  const transformDistrict = (districts: { [key: string]: number }) => {
    return Object.entries(districts).map(([name, projects]) => ({
      name,
      projects,
    }));
  };

  return (
    <>
      <div className='my-4'>
        <section className='my-4 grid md:grid-cols-2 xl:grid-cols-4 gap-4'>
          <TotalApplicationsWidget
            count={widgetsData?.totalApplications ?? 0}
          />
          <ToBeInspectedWidget count={widgetsData?.totalToBeInspected ?? 0} />
          <TotalDeniedRulesWidget
            count={widgetsData?.totalReceivedAssistance ?? 0}
          />
        </section>
        <Card title='Status Summary'>
          <>
            <div className='grid grid-cols-8 gap-6 py-6 px-6'>
              {!isObjectEmpty(EApplicationStatus) &&
                Object.keys(EApplicationStatus).map((s: any) => (
                  <div className='col-span-2 font-medium flex justify-between gap-6'>
                    <span>
                      {convertEnumToText(
                        EApplicationStatus[s as keyof typeof EApplicationStatus]
                      )}
                    </span>
                    <span>{widgetsData?.['statusSummary'][s] ?? 0}</span>
                  </div>
                ))}
            </div>
          </>
        </Card>
      </div>
      <div className='my-4'>
        <Card title='Project Location'>
          {applicationsGeoLocated.length === 0 && (
            <div className='w-full flex justify-center items-center h-screen/2'>
              <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
                <LocationMarkerIcon className='h-8 w-8' />
                <h1>No Projects</h1>
              </div>
              <p>There are no assigned projects to display.</p>
            </div>
          )}
          {applicationsGeoLocated.length > 0 && (
            <div className='w-full grid grid-cols-6 h-screen/2'>
              <div className='col-span-6 w-full border-l p-5'>
                {applicationsGeoLocated[0] && (
                  <GoogleMap
                    defaultCenter={{
                      lat: applicationsGeoLocated[0].coordinates.lat,
                      lng: applicationsGeoLocated[0].coordinates.lng,
                    }}
                  >
                    {applicationsGeoLocated?.map((res: any) => {
                      return (
                        <LocationMarker
                          key={res?.id}
                          lat={res?.coordinates.lat}
                          lng={res?.coordinates.lng}
                        >
                          <div className='relative'>
                            {selectedProject &&
                              selectedProject?.id === res?.id && (
                                <div
                                  onClick={() => setSelectedProject(undefined)}
                                  className='bg-white border rounded absolute p-3 bottom-4 left-5 w-56'
                                >
                                  <XIcon className='h-4 w-4 absolute right-1 top-1 text-neutralColor-normal hover:text-red-500' />
                                  <Link
                                    to={`${location.pathname.replace(
                                      'dashboard',
                                      'applications'
                                    )}/${
                                      selectedProject?.id
                                    }/application_details`}
                                  >
                                    <p className='font-semibold text-primaryColor-normal underline'>
                                      {selectedProject?.name}
                                    </p>
                                  </Link>
                                  <p className='text-xs'>
                                    {selectedProject?.fullAddress}
                                  </p>
                                  <div className='mt-2 text-sm flex justify-between gap-2'>
                                    <span>Email</span>
                                    <span className='font-semibold'>
                                      {selectedProject?.email}
                                    </span>
                                  </div>
                                  <div className='mt-2 text-sm flex justify-between gap-2'>
                                    <span>Phone</span>
                                    <span className='font-semibold'>
                                      <PhoneNumberFormatDisplay
                                        value={selectedProject?.phone || 'none'}
                                      />
                                    </span>
                                  </div>
                                </div>
                              )}
                            {res && (
                              <LocationMarkerIcon
                                onClick={() => setSelectedProject(res)}
                                className={
                                  selectedProject?.id === res?.id
                                    ? 'h-8 w-8 text-black'
                                    : 'h-8 w-8 text-red-500 '
                                }
                              />
                            )}
                          </div>
                        </LocationMarker>
                      );
                    })}
                  </GoogleMap>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>
      <div className='my-4'>
        <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
          <Card title='Marital Status'>
            <div className='p-5 flex flex-col items-center '>
              <div className='w-5/6 lg:w-5/5 h-80'>
                <PieChart
                  labels={widgetsData?.maritalStatus?.label}
                  datasets={convertIntoPercentage(
                    widgetsData?.maritalStatus?.dataSets
                  )}
                  chartTitle=''
                  showValue={true}
                />
              </div>
            </div>
          </Card>
        </section>
        <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
          <Card title='Gender'>
            <div className='p-5 flex flex-col items-center '>
              <div className='w-5/6 lg:w-5/5 h-80'>
                <PieChart
                  labels={widgetsData?.gender?.label}
                  datasets={convertIntoPercentage(
                    widgetsData?.gender?.dataSets
                  )}
                  chartTitle=''
                  showValue={true}
                />
              </div>
            </div>
          </Card>
          <Card title='Race'>
            <div className='p-5 flex flex-col items-center '>
              <div className='w-5/6 lg:w-5/5 h-80'>
                <PieChart
                  labels={widgetsData?.totalRaces?.label}
                  datasets={convertIntoPercentage(
                    widgetsData?.totalRaces?.dataSets
                  )}
                  chartTitle=''
                  showValue={true}
                />
              </div>
            </div>
          </Card>
        </section>
        <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
          <Card title='Disabled'>
            <div className='p-5 flex flex-col items-center '>
              <div className='w-5/6 lg:w-5/5 h-80'>
                <PieChart
                  labels={widgetsData?.totalDisabled?.label}
                  datasets={convertIntoPercentage(
                    widgetsData?.totalDisabled?.dataSets
                  )}
                  chartTitle=''
                  showValue={true}
                />
              </div>
            </div>
          </Card>
          <Card title='Income Breakdown'>
            <div className='p-5 flex flex-col items-center '>
              <div className='w-5/6 lg:w-5/5 h-80'>
                <PieChart
                  labels={widgetsData?.totalRaces?.label}
                  datasets={convertIntoPercentage(
                    widgetsData?.totalRaces?.dataSets
                  )}
                  chartTitle=''
                  showValue={true}
                />
              </div>
            </div>
          </Card>
        </section>
        <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
          <Card title='House Built Before 1978'>
            <div className='p-5 flex flex-col items-center '>
              <div className='w-5/6 lg:w-5/5 h-80'>
                <PieChart
                  labels={widgetsData?.totalHouseBuiltBefore1978?.label}
                  datasets={convertIntoPercentage(
                    widgetsData?.totalHouseBuiltBefore1978?.dataSets
                  )}
                  chartTitle=''
                  showValue={true}
                />
              </div>
            </div>
          </Card>
          <Card title='Female Head of Household'>
            <div className='p-5 flex flex-col items-center '>
              <div className='w-5/6 lg:w-5/5 h-80'>
                <PieChart
                  labels={widgetsData?.totalFemaleHeadOfHouseHold?.label}
                  datasets={convertIntoPercentage(
                    widgetsData?.totalFemaleHeadOfHouseHold?.dataSets
                  )}
                  chartTitle=''
                  showValue={true}
                />
              </div>
            </div>
          </Card>
        </section>
        <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
          <DistrictBreakdownDeniedWidget
            data={transformDistrict(widgetsData?.districtBreakdownDenied)}
          />
        </section>
      </div>
    </>
  );
}
